import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Button} from 'shells/button/button';
// @ts-expect-error - imported from .js/.jsx file
import {ReplyIcon} from 'shells/icon/svg/reply-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';

import type {
    DeleteEmailInput,
    DeleteEmail as DeleteEmailMutation,
    DeleteEmailVariables as DeleteEmailMutationVariables,
    UpdateEmail as UpdateEmailMutation,
    UpdateEmailVariables as UpdateEmailMutationVariables,
    EmailShareAndCreateContactsInput,
    Email as EmailFragment,
} from 'nutshell-graphql-types';

import {EmailRow} from '../email-row';
import DELETE_EMAIL from '../graphql/mutations/delete-email.graphql';
import UPDATE_EMAIL from '../graphql/mutations/update-email.graphql';
import {mapEmailFragmentToEmailMessage} from '../utils';

import styles from './inbox-row-item.module.css';

type OwnProps = {
    email: EmailFragment;
    isFirstOfDate: boolean;
    onEmailSharedChange: (
        proxy: DataProxy,
        update: {
            data?: UpdateEmailMutation;
        }
    ) => void;
    onEmailDeleted: (
        proxy: DataProxy,
        update: {
            data?: DeleteEmailMutation;
        }
    ) => void;
    onReply?: () => void;
};

type State = {
    isDeletingEmail: boolean;
};

type Props = OwnProps & {
    onUpdateEmail: (arg1: {
        payload: EmailShareAndCreateContactsInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: UpdateEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onDeleteEmail: (arg1: {
        payload: DeleteEmailInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: DeleteEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
};

class InboxRowItemComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isDeletingEmail: false,
        };
    }

    render() {
        const email = mapEmailFragmentToEmailMessage(this.props.email);

        return (
            <EmailRow
                key={this.props.email.id}
                emailListItem={email}
                isFirstOfDate={this.props.isFirstOfDate}
                status={
                    this.props.email.isShared
                        ? undefined
                        : {
                              level: 'hidden',
                              text: 'Private',
                              detail: 'This email is only visible to you',
                          }
                }
                isReceivedEmail={true}
                isDeletingEmail={this.state.isDeletingEmail}
                onDeleteConfirm={this.handleDeleteConfirm}
                onDeleteCancel={this.handleDeleteCancel}
                // @ts-expect-error
                actions={this.getActionMenuOptions()}
                detailLinkComponent={
                    <div className={styles['email-buttons-container']}>
                        {this.props.onReply ? (
                            <div className={styles['reply-button-container']}>
                                <Button onClick={this.props.onReply} variant='primary' size='big'>
                                    <StackLegacy spacing={4} isHorizontal={true}>
                                        <ReplyIcon fill='#fff' size={14} />
                                        Reply
                                    </StackLegacy>
                                </Button>
                            </div>
                        ) : undefined}
                        <Link variant='secondary' href={email.htmlUrl}>
                            View details
                        </Link>
                    </div>
                }
            />
        );
    }

    getActionMenuOptions = () => {
        const actions = [];
        if (this.props.email.isShareable) {
            actions.push({
                label: this.props.email.isShared ? 'Unshare' : 'Share',
                handleSelect: () => {
                    this.props.onUpdateEmail({
                        payload: {
                            emailId: this.props.email.id,
                            shouldShare: !this.props.email.isShared,
                        },
                        update: this.props.onEmailSharedChange,
                    });
                },
            });
        }

        const onReply = this.props.onReply;

        if (onReply) {
            actions.push({
                label: 'Reply',
                handleSelect: onReply,
            });
        }

        actions.push({
            label: 'Delete',
            variant: 'destructive',
            handleSelect: () => this.setState({isDeletingEmail: true}),
        });

        return actions;
    };

    handleDeleteCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});
    };

    handleDeleteConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});

        this.props.onDeleteEmail({
            payload: {
                email: {
                    id: this.props.email.id,
                },
            },
            update: this.props.onEmailDeleted,
        });
    };
}

class DeleteMutation extends Mutation<DeleteEmailMutation, DeleteEmailMutationVariables> {}
class UpdateMutation extends Mutation<UpdateEmailMutation, UpdateEmailMutationVariables> {}

const InboxRowItemGraphQL = (props: OwnProps & ContextRouter) => (
    <DeleteMutation mutation={DELETE_EMAIL}>
        {(deleteEmail) => (
            <UpdateMutation mutation={UPDATE_EMAIL}>
                {(updateEmail) => (
                    <InboxRowItemComponent
                        onReply={props.onReply}
                        email={props.email}
                        isFirstOfDate={props.isFirstOfDate}
                        onEmailSharedChange={props.onEmailSharedChange}
                        onEmailDeleted={props.onEmailDeleted}
                        onDeleteEmail={({payload, update}) =>
                            deleteEmail({variables: {payload}, update})
                        }
                        onUpdateEmail={({payload, update}) =>
                            updateEmail({variables: {payload}, update})
                        }
                    />
                )}
            </UpdateMutation>
        )}
    </DeleteMutation>
);

// TODO: Do we really need to wrap this with withRouter?  Doesn't seem to be used...
// @ts-expect-error
export const InboxRowItem = withRouter<OwnProps>(InboxRowItemGraphQL);
